import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

const COLORS = {
  "DDoS": "#EF4444",
  "Malware": "#A855F7",
  "Intrusion": "#F59E0B",
  "Brute Force": "#3B82F6",
};

const tooltipStyle = {
  contentStyle: { background: "#020617", border: "1px solid #1E293B", borderRadius: 2, fontFamily: "IBM Plex Mono", fontSize: 11 },
  labelStyle: { color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 10 },
};

export default function ThreatsByTypeChart() {
  const { tenant } = useTenant();
  const [data, setData] = useState([]);

  useEffect(() => {
    if (!tenant) return;
    api.get(`/soc/threats-by-type?tenant=${tenant.id}`).then(({ data }) => setData(data));
  }, [tenant]);

  const total = data.reduce((a, d) => a + d.blocked, 0);

  return (
    <div className="p-5" data-testid="threats-by-type-chart">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500">Total Blocked · 7d</div>
          <div className="font-display text-3xl font-black tracking-tight text-slate-100">{total.toLocaleString()}</div>
        </div>
        <div className="text-[10px] font-mono text-emerald-400 flex items-center gap-1">
          <ArrowUpRight className="w-3 h-3" /> auto-blocked at edge
        </div>
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 8 }}>
          <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="type" stroke="#94A3B8" fontSize={10} tickLine={false} />
          <YAxis stroke="#475569" fontSize={10} />
          <Tooltip {...tooltipStyle} />
          <Bar dataKey="blocked" radius={[2, 2, 0, 0]}>
            {data.map((d, i) => <Cell key={i} fill={COLORS[d.type] || "#3B82F6"} />)}
            <LabelList dataKey="blocked" position="top" fill="#94A3B8" fontSize={10} fontFamily="IBM Plex Mono" />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="grid grid-cols-4 gap-2 mt-4">
        {data.map((d) => (
          <div key={d.type} className="border border-slate-800 bg-[#020617] p-2">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: COLORS[d.type] }} />
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{d.type}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="font-mono font-bold text-sm text-slate-100">{d.blocked.toLocaleString()}</span>
              <span className={`text-[10px] font-mono flex items-center ${d.trend >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {d.trend >= 0 ? <ArrowUpRight className="w-2.5 h-2.5" /> : <ArrowDownRight className="w-2.5 h-2.5" />}
                {Math.abs(d.trend)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
